import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Label } from 'reactstrap';
import { isEmpty, noop } from 'lodash-es';

// Models
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleVinModel, buildStylesBasicPathFromVin } from 'client/data/models/vehicle-vin';

// Utils
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';

// Constants
import { APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID } from 'client/site-modules/shared/constants/appraisal/appraisal';

// Context
import { useAppraisalTabsContext } from 'site-modules/shared/components/appraisal/appraisal-tabs/appraisal-tabs-context';
import {
  appraisalTabViewPropType,
  getResolvedAppraisalTabViewProps,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tab-view';

// Components
import { AppraiseTabContentFormWrapper } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraise-tab-content-form-wrapper';
import { FindVinModal } from 'site-modules/shared/components/find-vin-modal/find-vin-modal';

export function VinTabContentUI({
  inputSlot = null,
  submitButtonSlot = null,
  isVinValid = false,
  wasFormSubmitted = false,
  vinStyles = null,
  isLookUpStyleView = false,
  isFullWidth = false,
  profileOfferVin = '',
  hasAddressToVinDrawer = false,
  isBackgroundStyleTabEnabled = false,
  resetIsSubmitting,
  isSubmitting = false,
  isDecodedVinInvalid = false,
  onFindVinModalOpen = noop,
  onFindVinModalClose = noop,
  vinInputId = 'vin-input',
  view = null,
}) {
  const {
    mmy,
    vin,
    setMmy: setContextMmy,
    setVinDecoderStyles: setContextVinDecoderStyles,
    setIsReadyForRedirect,
    isProjectEveStyle,
  } = useAppraisalTabsContext();

  let firstVinStyleId;

  if (!isEmpty(vinStyles)) {
    firstVinStyleId = vinStyles?.results?.[0]?.styleId ?? null;
  }

  useEffect(() => {
    setContextVinDecoderStyles(vinStyles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstVinStyleId]); // Use the first ID in the hook dependencies to prevent extra calls

  useEffect(() => {
    if (isDecodedVinInvalid && isSubmitting) {
      resetIsSubmitting();
    }
  }, [isDecodedVinInvalid, isSubmitting, resetIsSubmitting]);

  const isReadyForUpdate = !!vinStyles && wasFormSubmitted && isVinValid && isSubmitting;
  const hasStyles = !!vinStyles && vinStyles?.results?.length;

  useEffect(() => {
    if (!isReadyForUpdate) {
      return;
    }

    if (!hasStyles) return;

    const { makeNiceId: makeSlug, modelNiceId: modelSlug, year } = vinStyles?.results?.[0];
    setContextMmy({ make: makeSlug, model: modelSlug, year });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadyForUpdate]);

  // Make sure all data is set and initiate the redirect
  useEffect(() => {
    if (isReadyForUpdate && !isEmpty(mmy) && !!vin) {
      delayHeavyTask(() => {
        setIsReadyForRedirect(true);
      });
    }
  }, [isReadyForUpdate, mmy, setIsReadyForRedirect, vin]);

  const resolvedView = getResolvedAppraisalTabViewProps({
    view,
    isLookUpStyleView,
    isFullWidth,
    profileOfferVin,
    hasAddressToVinDrawer,
    isBackgroundStyleTabEnabled,
  });

  const isFindVinModalDisplayed = !resolvedView.profileOfferVin && !resolvedView.isLookUpStyleView;

  const btnColLgSize = resolvedView.isBackgroundStyleTabEnabled || isProjectEveStyle ? 4 : 12;
  const vinInputColLgSize = resolvedView.isBackgroundStyleTabEnabled || isProjectEveStyle ? 6 : 12;

  return (
    <div className="appraise-by-vin-tab">
      <Label className="size-12" for={vinInputId}>
        Vehicle Identification Number (VIN)
      </Label>
      <Row>
        <Col xs={12} lg={vinInputColLgSize}>
          <AppraiseTabContentFormWrapper isFullWidth={resolvedView.isFullWidth}>
            <Row>
              <Col xs={12}>{inputSlot}</Col>
            </Row>
          </AppraiseTabContentFormWrapper>
        </Col>
        <Col xs={12} lg={btnColLgSize}>
          {isFindVinModalDisplayed &&
            !resolvedView.hasAddressToVinDrawer &&
            !resolvedView.isBackgroundStyleTabEnabled &&
            !isProjectEveStyle && (
              <FindVinModal
                className="mb-1 p-0 border-0 text-primary-darker text-transform-none size-16 find-vin-modal-open-btn"
                creativeId={APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID}
                onOpen={onFindVinModalOpen}
                onClose={onFindVinModalClose}
              />
            )}
          {submitButtonSlot}
        </Col>
      </Row>
    </div>
  );
}

VinTabContentUI.propTypes = {
  inputSlot: PropTypes.node,
  submitButtonSlot: PropTypes.node,
  isVinValid: PropTypes.bool,
  wasFormSubmitted: PropTypes.bool,
  vinStyles: PropTypes.shape({
    matchType: PropTypes.string,
    results: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isLookUpStyleView: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  profileOfferVin: PropTypes.string,
  hasAddressToVinDrawer: PropTypes.bool,
  isBackgroundStyleTabEnabled: PropTypes.bool,
  resetIsSubmitting: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  isDecodedVinInvalid: PropTypes.bool,
  onFindVinModalOpen: PropTypes.func,
  onFindVinModalClose: PropTypes.func,
  vinInputId: PropTypes.string,
  view: appraisalTabViewPropType,
};

export const stateToPropsConfig = {
  vinStyles: bindToPath(
    ({ vin, vinInputValue }) => (vin === vinInputValue ? buildStylesBasicPathFromVin({ vin }) : ''),
    VehicleVinModel
  ),
};

export const VinTabContent = connectToModel(VinTabContentUI, stateToPropsConfig);
